/**
 * Minimal parser for the assistant's replies: plain text with optional
 * GitHub-style pipe tables. Nothing else is interpreted, so a reply can never
 * inject markup; it is only ever split into text blocks and table blocks.
 */

export type ChatTableBlock = {
  kind: 'table';
  header: string[];
  rows: string[][];
};

export type ChatTextBlock = {
  kind: 'text';
  text: string;
};

export type ChatBlock = ChatTableBlock | ChatTextBlock;

const SEPARATOR_CELL = /^:?-{3,}:?$/;

function isTableLine(line: string): boolean {
  const trimmed = line.trim();
  return trimmed.startsWith('|') && trimmed.endsWith('|') && trimmed.length > 2;
}

function splitCells(line: string): string[] {
  const trimmed = line.trim().replace(/^\|/, '').replace(/\|$/, '');
  return trimmed.split('|').map((cell) => cell.trim());
}

function isSeparatorRow(cells: string[]): boolean {
  return cells.length > 0 && cells.every((cell) => SEPARATOR_CELL.test(cell.replace(/\s+/g, '')));
}

/** Strip the light inline markdown the model tends to add (bold, code) so cells read cleanly. */
export function stripInlineMarkdown(text: string): string {
  return text.replace(/\*\*([^*]+)\*\*/g, '$1').replace(/`([^`]+)`/g, '$1');
}

/**
 * Split a reply into text and table blocks. A table is two or more consecutive
 * pipe lines where the second is a `---` separator row; anything else stays text.
 */
export function parseChatBlocks(content: string): ChatBlock[] {
  const lines = content.replace(/\r\n/g, '\n').split('\n');
  const blocks: ChatBlock[] = [];
  let text: string[] = [];

  const flushText = () => {
    const joined = text.join('\n').trim();
    if (joined) blocks.push({ kind: 'text', text: stripInlineMarkdown(joined) });
    text = [];
  };

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (isTableLine(line) && i + 1 < lines.length && isTableLine(lines[i + 1])) {
      const header = splitCells(line);
      const second = splitCells(lines[i + 1]);
      if (isSeparatorRow(second)) {
        const rows: string[][] = [];
        let j = i + 2;
        while (j < lines.length && isTableLine(lines[j])) {
          const cells = splitCells(lines[j]);
          if (!isSeparatorRow(cells)) {
            // Pad or trim to the header width so the table stays rectangular.
            const fixed = header.map((_, col) => stripInlineMarkdown(cells[col] ?? ''));
            rows.push(fixed);
          }
          j += 1;
        }
        flushText();
        blocks.push({ kind: 'table', header: header.map(stripInlineMarkdown), rows });
        i = j;
        continue;
      }
    }
    text.push(line);
    i += 1;
  }
  flushText();
  return blocks;
}

/** True when a reply contains at least one table block. */
export function hasChatTable(content: string): boolean {
  return parseChatBlocks(content).some((block) => block.kind === 'table');
}
